import { useState, useEffect, useCallback, useRef } from "react"
import {
    Box,
    Button,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    LinearProgress,
    Alert,
    Chip,
} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import CloudDownloadIcon from "@mui/icons-material/CloudDownload"
import DeleteIcon from "@mui/icons-material/Delete"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import api from "../../api"
import { getSession } from "../../auth"
import useNotification from "../Notification"
import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"
import axios from "axios"

const formatFileSize = (bytes) => {
    if (!bytes) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
}

const hasFileTransfer = (dataTransfer) => {
    return Array.from(dataTransfer?.types || []).includes("Files")
}

const getFilesFromTransfer = (dataTransfer) => {
    if (!dataTransfer) return []

    const itemFiles = Array.from(dataTransfer.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter(Boolean)

    if (itemFiles.length > 0) return itemFiles

    return Array.from(dataTransfer.files || [])
}

const getFileCountLabel = (files) => {
    return files.length === 1 ? `"${files[0].name}"` : `${files.length} arquivos`
}

const OpportunityAttachments = ({ opportunityId, journeyId, disabled, onBusyChange }) => {
    const [attachments, setAttachments] = useState([])
    const [pendingFiles, setPendingFiles] = useState([])
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [dragActive, setDragActive] = useState(false)
    const [deleteConfirmation, setDeleteConfirmation] = useState(null)
    const fileInputRef = useRef(null)
    const uploadingRef = useRef(false)
    const dragCounterRef = useRef(0)

    const enterprise = getSession().profile.enterprise
    const maxMb = enterprise.opportunity_attachment_max_mb || 10

    const isNew = !opportunityId || opportunityId === 0

    useEffect(() => {
        if (onBusyChange) {
            onBusyChange(uploading || pendingFiles.length > 0)
        }
    }, [uploading, pendingFiles, onBusyChange])

    const getBaseUrl = useCallback((oppId) => {
        return `api/enterprises/${enterprise.id}/journeys/${journeyId}/opportunities/${oppId}/attachments`
    }, [enterprise.id, journeyId])

    const fetchAttachments = useCallback(async () => {
        if (isNew) return
        try {
            const response = await api.get(`${getBaseUrl(opportunityId)}/`)
            if (response.status === 200) {
                setAttachments(response.data)
            }
        } catch (err) {
            console.error("Erro ao carregar anexos:", err)
        }
    }, [opportunityId, isNew, getBaseUrl])

    useEffect(() => {
        fetchAttachments()
    }, [fetchAttachments])

    const uploadFile = useCallback(async (file, oppId) => {
        const baseUrl = getBaseUrl(oppId)

        const presignResponse = await api.post(`${baseUrl}/presign/`, {
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            sizeBytes: file.size,
        })

        if (presignResponse.status !== 200) {
            throw new Error(presignResponse.data?.detail || "Erro ao gerar URL de upload.")
        }

        const { attachmentId, uploadUrl, requiredHeaders } = presignResponse.data

        await axios.put(uploadUrl, file, {
            headers: {
                'Content-Type': requiredHeaders['Content-Type'] || file.type || 'application/octet-stream',
            },
            transformRequest: [(data) => data],
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: (progressEvent) => {
                const percent = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                )
                setUploadProgress(percent)
            },
        })

        const completeResponse = await api.post(`${baseUrl}/complete/`, {
            attachmentId,
        })

        if (completeResponse.status === 200) {
            return completeResponse.data
        }
        throw new Error(completeResponse.data?.detail || "Erro ao confirmar upload.")
    }, [getBaseUrl])

    useEffect(() => {
        if (isNew || pendingFiles.length === 0 || uploadingRef.current) return

        const uploadPending = async () => {
            uploadingRef.current = true
            setUploading(true)

            const filesToUpload = [...pendingFiles]
            setPendingFiles([])
            let uploadedCount = 0

            for (const file of filesToUpload) {
                try {
                    setUploadProgress(0)
                    const result = await uploadFile(file, opportunityId)
                    setAttachments((prev) => [result, ...prev])
                    uploadedCount += 1
                } catch (err) {
                    console.error("Erro no upload pendente:", err)
                    useNotification(
                        "Erro",
                        `Erro ao enviar "${file.name}": ${err?.response?.data?.detail || err.message}`,
                        "danger"
                    )
                }
            }

            setUploading(false)
            setUploadProgress(0)
            uploadingRef.current = false

            if (uploadedCount > 0) {
                useNotification(
                    "Sucesso!",
                    uploadedCount === 1 ? "Arquivo anexado." : "Arquivos anexados.",
                    "success"
                )
            }
        }

        uploadPending()
    }, [opportunityId, isNew, pendingFiles, uploadFile, uploading])

    const handleFiles = useCallback((files) => {
        const selectedFiles = Array.from(files || []).filter(Boolean)
        if (selectedFiles.length === 0) return

        if (fileInputRef.current) fileInputRef.current.value = ""

        const maxBytes = maxMb * 1024 * 1024

        const validFiles = []
        const oversizedFiles = []

        selectedFiles.forEach((file) => {
            if (file.size > maxBytes) {
                oversizedFiles.push(file)
                return
            }

            validFiles.push(file)
        })

        if (oversizedFiles.length > 0) {
            useNotification(
                "Atenção!",
                oversizedFiles.length === 1
                    ? `"${oversizedFiles[0].name}" excede o limite de ${maxMb} MB.`
                    : `${oversizedFiles.length} arquivos excedem o limite de ${maxMb} MB.`,
                "warning"
            )
        }

        if (validFiles.length === 0) {
            return
        }

        setPendingFiles((prev) => [...prev, ...validFiles])

        if (isNew) {
            useNotification(
                "Arquivo na fila",
                `${getFileCountLabel(validFiles)} será enviado ao salvar a oportunidade.`,
                "info"
            )
            return
        }

        if (uploading) {
            useNotification(
                "Arquivo na fila",
                `${getFileCountLabel(validFiles)} será enviado em seguida.`,
                "info"
            )
        }
    }, [isNew, maxMb, uploading])

    const handleFileSelect = (event) => {
        handleFiles(event.target.files)
    }

    useEffect(() => {
        const handlePaste = (event) => {
            if (deleteConfirmation) return

            const files = getFilesFromTransfer(event.clipboardData)
            if (files.length === 0) return

            event.preventDefault()
            handleFiles(files)
        }

        document.addEventListener("paste", handlePaste)
        return () => document.removeEventListener("paste", handlePaste)
    }, [deleteConfirmation, handleFiles])

    useEffect(() => {
        const resetDragState = () => {
            dragCounterRef.current = 0
            setDragActive(false)
        }

        const handleDragEnter = (event) => {
            if (!hasFileTransfer(event.dataTransfer)) return

            event.preventDefault()
            dragCounterRef.current += 1
            setDragActive(true)
        }

        const handleDragOver = (event) => {
            if (!hasFileTransfer(event.dataTransfer)) return

            event.preventDefault()
            event.dataTransfer.dropEffect = "copy"
            setDragActive(true)
        }

        const handleDragLeave = (event) => {
            if (!hasFileTransfer(event.dataTransfer)) return

            event.preventDefault()
            dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)

            if (dragCounterRef.current === 0) {
                setDragActive(false)
            }
        }

        const handleDrop = (event) => {
            if (!hasFileTransfer(event.dataTransfer)) return

            event.preventDefault()
            const files = getFilesFromTransfer(event.dataTransfer)
            resetDragState()

            if (deleteConfirmation || files.length === 0) return

            handleFiles(files)
        }

        document.addEventListener("dragenter", handleDragEnter)
        document.addEventListener("dragover", handleDragOver)
        document.addEventListener("dragleave", handleDragLeave)
        document.addEventListener("drop", handleDrop)
        document.addEventListener("dragend", resetDragState)
        window.addEventListener("blur", resetDragState)

        return () => {
            document.removeEventListener("dragenter", handleDragEnter)
            document.removeEventListener("dragover", handleDragOver)
            document.removeEventListener("dragleave", handleDragLeave)
            document.removeEventListener("drop", handleDrop)
            document.removeEventListener("dragend", resetDragState)
            window.removeEventListener("blur", resetDragState)
        }
    }, [deleteConfirmation, handleFiles])

    const handleRemovePending = (index) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index))
    }

    const handleDownload = async (attachment) => {
        try {
            const response = await api.get(
                `${getBaseUrl(opportunityId)}/${attachment.id}/download/`
            )
            if (response.status === 200 && response.data.downloadUrl) {
                window.open(response.data.downloadUrl, "_blank")
            }
        } catch (err) {
            useNotification("Erro", "Erro ao baixar o arquivo.", "danger")
        }
    }

    const handleDelete = async (attachment) => {
        try {
            const response = await api.delete(
                `${getBaseUrl(opportunityId)}/${attachment.id}/remove/`
            )
            if (response.status === 204) {
                setAttachments((prev) =>
                    prev.filter((a) => a.id !== attachment.id)
                )
                useNotification("Sucesso!", "Anexo removido.", "success")
            }
        } catch (err) {
            useNotification("Erro", "Erro ao remover o anexo.", "danger")
        } finally {
            setDeleteConfirmation(null)
        }
    }

    return (
        <Box
            sx={{
                mt: 2,
                mb: 2,
                position: "relative",
                borderRadius: 1,
                outline: dragActive ? "2px dashed" : "2px dashed transparent",
                outlineColor: dragActive ? "primary.main" : "transparent",
                outlineOffset: 4,
                backgroundColor: dragActive ? "rgba(25, 118, 210, 0.06)" : "transparent",
                transition: "background-color 120ms ease, outline-color 120ms ease",
            }}
        >
            {dragActive && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: -6,
                        zIndex: 2,
                        borderRadius: 1,
                        bgcolor: "rgba(255, 255, 255, 0.88)",
                        border: "2px dashed",
                        borderColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        color: "primary.main",
                        fontWeight: "bold",
                        pointerEvents: "none",
                    }}
                >
                    <AttachFileIcon />
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Solte para anexar
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    <AttachFileIcon
                        sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }}
                    />
                    Anexos
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                    startIcon={<AttachFileIcon />}
                >
                    Anexar arquivo
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileSelect}
                    />
                </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Máx. {maxMb} MB por arquivo
            </Typography>

            {uploading && (
                <Box sx={{ mb: 1 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary">
                        Enviando... {uploadProgress}%
                    </Typography>
                </Box>
            )}

            {pendingFiles.length > 0 && (
                <List dense sx={{ bgcolor: "background.paper", borderRadius: 1, mb: 1 }}>
                    {pendingFiles.map((file, index) => (
                        <ListItem key={`pending-${index}`} sx={{ pr: 8 }}>
                            <HourglassEmptyIcon
                                sx={{ mr: 1, color: "warning.main", fontSize: 20 }}
                            />
                            <ListItemText
                                primary={file.name}
                                secondary={
                                    <>
                                        {formatFileSize(file.size)}
                                        {" • Aguardando salvar"}
                                    </>
                                }
                                primaryTypographyProps={{
                                    noWrap: true,
                                    sx: { maxWidth: "250px" },
                                }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleRemovePending(index)}
                                    title="Remover da fila"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}

            {attachments.length > 0 && (
                <List dense sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
                    {attachments.map((att) => (
                        <ListItem key={att.id} sx={{ pr: 12 }}>
                            <InsertDriveFileIcon
                                sx={{ mr: 1, color: "action.active", fontSize: 20 }}
                            />
                            <ListItemText
                                primary={att.original_filename}
                                secondary={
                                    <>
                                        {formatFileSize(att.size_bytes)}
                                        {att.uploaded_by_name &&
                                            ` • ${att.uploaded_by_name}`}
                                        {att.created_at &&
                                            ` • ${new Date(att.created_at).toLocaleDateString("pt-BR")}`}
                                    </>
                                }
                                primaryTypographyProps={{
                                    noWrap: true,
                                    sx: { maxWidth: "250px" },
                                }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleDownload(att)}
                                    title="Baixar"
                                >
                                    <CloudDownloadIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => setDeleteConfirmation(att)}
                                    title="Remover"
                                    sx={{ ml: 0.5 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}

            {attachments.length === 0 && pendingFiles.length === 0 && !uploading && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                    Nenhum anexo.
                </Alert>
            )}

            {deleteConfirmation && (
                <ConfirmationDialogCaptcha
                    open={!!deleteConfirmation}
                    title="Confirmação"
                    description={
                        <Box>
                            Para remover o anexo "{deleteConfirmation.original_filename}", digite:{" "}
                            <span style={{ fontWeight: "bold" }}>remover</span> no campo
                            abaixo.
                        </Box>
                    }
                    handleClose={() => setDeleteConfirmation(null)}
                    handleSubmit={() => handleDelete(deleteConfirmation)}
                    captcha="remover"
                />
            )}
        </Box>
    )
}

export default OpportunityAttachments
